import React from "react";
import {
    Box,
    Tooltip,
    IconButton,
} from "@mui/material";
import ArchiveIcon from "@mui/icons-material/Archive";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import { VscEditorLayout } from "react-icons/vsc";
import FileActionButtons from "./FileActionButtons";
import {ALLOWED_FILE_STATUS_TRANSITIONS} from "../../utlis/constant";
import {SUPER_ADMIN} from "../../constants/constants";
import StatusChip from "../../reusableComponents/status/StatusChip";
import {openTheEditor} from "../payment/helper";
import PropTypes from "prop-types";
import {getQualityStyles} from "../../utlis/generalUtils";
import Typography from "@mui/material/Typography";

export const buildFileRow = ({
                                 file,
                                 role,
                                 currentLang,
                                 editingFileId,
                                 hoveredRowId,
                                 editedNotes,
                                 setEditedNotes,
                                 setHoveredRowId,
                                 startEditingNotes,
                                 saveNotes,
                                 cancelEditingNotes,
                                 handleStatusChange,
                                 openConfirmation,
                                 openSelectOCRTypeConfirmation,
                                 formatDurationValue,
                                 loadingQualityId
                             }) => {
    const possibleFileStatusesTransitions =
        ALLOWED_FILE_STATUS_TRANSITIONS[file.status] || [];

    const label = file.quality_label || "Unknown";
    const { color, bg, icon } = getQualityStyles(label);

    const baseRow = {
        id: file.id,
        "File Name": (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, maxWidth: 320 }}>
                <ArchiveIcon sx={{ color: "#6c63ff", flexShrink: 0 }} />

                <Tooltip title={file.name} arrow placement="top">
                    <Typography
                        variant="body2"
                        sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            cursor: "pointer",
                            maxWidth: "250px",
                        }}
                    >
                        {file.name.length > 35
                            ? `${file.name.substring(0, 35)}...`
                            : file.name}
                    </Typography>
                </Tooltip>
            </Box>
        ),
        Status: (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                {possibleFileStatusesTransitions.length > 0 ? (
                    <Tooltip
                        title={role === SUPER_ADMIN ? "Click to update status" : "File status"}
                        arrow
                        placement="top"
                    >
                        <Box>
                            <StatusChip
                                status={file.status}
                                clickable={role === SUPER_ADMIN}
                                label={file.status}
                                availableStatuses={possibleFileStatusesTransitions}
                                onStatusChange={(newStatus) =>
                                    handleStatusChange(file.id, newStatus)
                                }
                            />
                        </Box>
                    </Tooltip>
                ) : (
                    <StatusChip status={file.status} />
                )}
            </Box>
        ),
        Pages: (
            <Box sx={{ textAlign: "center" }}>
                {file.num_pages ?? "-"}
            </Box>
        ),
        StatusTag: file.status,
        Quality:
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.2,
                        py: 0.4,
                        borderRadius: "999px",
                        backgroundColor: bg,
                        color: color,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        width: "fit-content",
                    }}
                >
                    {icon}
                    {label}
                </Box>
            </Box>
    };

    if (role !== SUPER_ADMIN) {
        return {
            ...baseRow,
            Actions: (
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                    {[
                        "completed",
                        "delivered",
                        "accepted",
                    ].includes(file.status) && (
                        <Tooltip title="Open Editor">
                            <IconButton
                                onClick={() => openTheEditor(file.digitization_id, currentLang)}
                            >
                                <VscEditorLayout />
                            </IconButton>
                        </Tooltip>
                    )}

                    {file.digitization_data_path && (
                        <Tooltip title="Download Digitization">
                            <IconButton
                                component="a"
                                href={file.digitization_data_path}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        };
    }

    return {
        ...baseRow,
        "Last Execution Date": (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                {file.last_execution_date
                    ? new Date(file.last_execution_date).toLocaleString()
                    : "-"}
            </Box>
        ),
        "Execution Time": (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                {file.execution_time
                    ? formatDurationValue(file.execution_time)
                    : "-"}
            </Box>
        ),
        "Execution Count": (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                {file.execution_count || 0}
            </Box>
        ),
        "File Type": (
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: editingFileId === file.id ? "flex-start" : "center",
                    gap: 1,
                }}
                onMouseEnter={() => editingFileId === null && setHoveredRowId(file.id)}
                onMouseLeave={() => editingFileId === null && setHoveredRowId(null)}
            >
                {editingFileId === file.id ? (
                    <>
            <textarea
                value={editedNotes}
                onChange={(e) => setEditedNotes(e.target.value)}
                autoFocus
                rows={1}
            />
                        <IconButton size="small" color="success" onClick={() => saveNotes(file.id)}>
                            <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={cancelEditingNotes}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </>
                ) : (
                    <>
                        <Box sx={{ flex: 1 }}>{file.notes || "-"}</Box>
                        {hoveredRowId === file.id && (
                            <Tooltip title="Edit notes">
                                <IconButton size="small" onClick={() => startEditingNotes(file)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </>
                )}
            </Box>
        ),
        "Tables (%)": (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                {file.table_percentage}
            </Box>
        ),
        "Digitization Cost ($)": (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                {file.digitization_estimated_cost != null
                    ? file.digitization_estimated_cost.toFixed(3)
                    : "-"}
            </Box>
        ),
        Actions: (
            <FileActionButtons
                file={file}
                currentLang={currentLang}
                openConfirmation={openConfirmation}
                openSelectOCRTypeConfirmation={openSelectOCRTypeConfirmation}
                loadingQualityId={loadingQualityId}
            />
        ),
    };
};

buildFileRow.propTypes = {
    file: PropTypes.object.isRequired,
    role: PropTypes.string.isRequired,
    currentLang: PropTypes.string.isRequired,
    editingFileId: PropTypes.number,
    hoveredRowId: PropTypes.number,
    editedNotes: PropTypes.string.isRequired,
    setEditedNotes: PropTypes.func.isRequired,
    setHoveredRowId: PropTypes.func.isRequired,
    startEditingNotes: PropTypes.func.isRequired,
    saveNotes: PropTypes.func.isRequired,
    cancelEditingNotes: PropTypes.func.isRequired,
    handleStatusChange: PropTypes.func.isRequired,
    openConfirmation: PropTypes.func.isRequired,
    openSelectOCRTypeConfirmation: PropTypes.func.isRequired,
    formatDurationValue: PropTypes.func.isRequired,
    loadingQualityId: PropTypes.number
};